import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Divider, Grid, Header, Icon, Label, Loader, Menu, Modal, Segment, Table } from 'semantic-ui-react';
import moment from 'moment';
import { Programming } from '../../models/programming';
import { ProgrammingLocationDto } from '../../models/programming-location-dto';
import useProgrammingService from '../../services/backoffice/hooks/useProgrammingService';
import StringUtils from '../../shared/utils/string-utils';
import {
    formatEstimatedArrival,
    getTrackingStatusColor,
    getTrackingStatusLabel
} from '../../shared/utils/torre-tracking-utils';
import {
    formatActualUtcAsSaoPaulo,
    formatProgrammingDateTime,
    getProgrammingTimelineStages,
    resolveIsGoAndReturn,
} from '../../utils/programming-timeline-utils';
import ProgrammingTripClosureSummary from './ProgrammingTripClosureSummary';

interface ProgrammingDetailsModalProps {
    open: boolean;
    programmingId: string | null;
    initialProgramming?: Programming;
    onClose: () => void;
    /** Demo local — exibe histórico fixo sem chamar a API. */
    demoAuditLogs?: import('../../models/programming').ProgrammingAuditLogEntry[];
    initialActiveTab?: 'detalhes' | 'alteracoes';
}

function toLocationDto(programming: Programming, destination?: Partial<ProgrammingLocationDto>): ProgrammingLocationDto {
    return {
        id: programming.id,
        mainVehiclePlateName: programming.mainVehiclePlateName,
        semiTrailer1Name: programming.semiTrailer1Name,
        semiTrailer2Name: programming.semiTrailer2Name,
        driverName: programming.driverName,
        clientName: programming.clientName,
        productName: programming.productName,
        volume: programming.volume,
        travelType: String(programming.travelType),
        status: String(programming.status),
        originReferenceName: programming.originReferenceName,
        destinationReferenceName: programming.destinationReferenceName,
        returnDestinationReferenceName: programming.returnDestinationReferenceName,
        expectedDepartureTime: programming.expectedDepartureTime,
        expectedArrivalTime: programming.expectedArrivalTime,
        expectedOriginDepartureAt: programming.expectedOriginDepartureAt,
        expectedDestinationArrivalAt: programming.expectedDestinationArrivalAt,
        expectedDestinationDepartureAt: programming.expectedDestinationDepartureAt,
        expectedReturnArrivalAt: programming.expectedReturnArrivalAt,
        actualOriginEnteredAt: programming.actualOriginEnteredAt,
        actualOriginExitedAt: programming.actualOriginExitedAt,
        actualDestinationEnteredAt: programming.actualDestinationEnteredAt,
        actualDestinationExitedAt: programming.actualDestinationExitedAt,
        actualReturnEnteredAt: programming.actualReturnEnteredAt,
        actualReturnExitedAt: programming.actualReturnExitedAt,
        isGoAndReturn: resolveIsGoAndReturn(programming),
        currentLatitude: programming.currentLatitude,
        currentLongitude: programming.currentLongitude,
        currentAddressInfo: programming.currentAddressInfo,
        lastLocationUpdate: programming.lastLocationUpdate ? new Date(programming.lastLocationUpdate) : undefined,
        destinationLatitude: destination?.destinationLatitude,
        destinationLongitude: destination?.destinationLongitude,
        latitude: destination?.latitude ?? programming.currentLatitude,
        longitude: destination?.longitude ?? programming.currentLongitude,
        addressInfo: destination?.addressInfo ?? programming.currentAddressInfo,
    };
}

const ProgrammingDetailsModal: React.FC<ProgrammingDetailsModalProps> = ({
    open,
    programmingId,
    initialProgramming,
    onClose,
    demoAuditLogs,
    initialActiveTab = 'detalhes',
}) => {
    const { getProgrammingById, getProgrammingLocations, getProgrammingAuditLogs } = useProgrammingService();
    const [programming, setProgramming] = useState<Programming | null>(null);
    const [locationDto, setLocationDto] = useState<ProgrammingLocationDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'detalhes' | 'alteracoes'>(initialActiveTab);
    const [auditLogs, setAuditLogs] = useState<import('../../models/programming').ProgrammingAuditLogEntry[]>([]);
    const [auditLoading, setAuditLoading] = useState(false);

    const seedFromInitial = useCallback(() => {
        if (!initialProgramming || initialProgramming.id !== programmingId) return;
        setProgramming(initialProgramming);
        setLocationDto(toLocationDto(initialProgramming));
    }, [initialProgramming, programmingId]);

    const loadDetails = useCallback(async () => {
        if (!programmingId) return;

        setLoading(true);
        try {
            // Fetches separados: falha de locations não pode zerar detalhes já seeded / ById.
            let resolved: Programming | null = null;
            try {
                const details = await getProgrammingById(programmingId);
                resolved = details || null;
            } catch {
                resolved = null;
            }

            if (resolved) {
                setProgramming(resolved);
                setLocationDto(prev => toLocationDto(resolved!, prev ?? undefined));
            } else if (initialProgramming && initialProgramming.id === programmingId) {
                setProgramming(initialProgramming);
                setLocationDto(prev => prev ?? toLocationDto(initialProgramming));
            }

            try {
                const locations = await getProgrammingLocations({
                    mainVehiclePlateId: initialProgramming?.mainVehiclePlateId,
                    perPage: 0,
                    page: 1,
                });
                const freshLocation = Array.isArray(locations)
                    ? locations.find(item => item.id === programmingId)
                    : undefined;

                if (freshLocation) {
                    setProgramming(prev => {
                        const base = resolved || prev || initialProgramming;
                        if (!base) return prev;
                        const nextDto = toLocationDto(base, freshLocation);
                        setLocationDto(nextDto);
                        return base;
                    });
                }
            } catch {
                // locations opcional — mantém seed / ById
            }
        } finally {
            setLoading(false);
        }
    }, [getProgrammingById, getProgrammingLocations, initialProgramming, programmingId]);

    useEffect(() => {
        if (!open || !programmingId) {
            setProgramming(null);
            setLocationDto(null);
            setActiveTab(initialActiveTab);
            setAuditLogs([]);
            return;
        }

        setActiveTab(initialActiveTab);

        seedFromInitial();
        loadDetails();
        const interval = setInterval(loadDetails, 30000);
        return () => clearInterval(interval);
    }, [open, programmingId, seedFromInitial, loadDetails]);

    useEffect(() => {
        if (!open || !programmingId || activeTab !== 'alteracoes') return;
        if (demoAuditLogs) {
            setAuditLogs(demoAuditLogs);
            setAuditLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            setAuditLoading(true);
            try {
                const logs = await getProgrammingAuditLogs(programmingId);
                if (!cancelled) setAuditLogs(logs);
            } catch {
                if (!cancelled) setAuditLogs([]);
            } finally {
                if (!cancelled) setAuditLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, programmingId, activeTab, getProgrammingAuditLogs, demoAuditLogs]);

    const hasCoordinates = useMemo(() => {
        if (!locationDto) return false;
        const lat = locationDto.currentLatitude ?? locationDto.latitude;
        const lng = locationDto.currentLongitude ?? locationDto.longitude;
        return lat !== undefined && lng !== undefined;
    }, [locationDto]);

    const statusLabel = programming ? getTrackingStatusLabel(programming.status) : '';
    const statusColor = programming ? getTrackingStatusColor(programming.status) : '#95a5a6';
    const timelineStages = programming ? getProgrammingTimelineStages(programming) : [];

    return (
        <Modal open={open} onClose={onClose} size="large" closeIcon>
            <Modal.Header>
                <Icon name="map marker alternate" />
                Detalhes da programação
                {programming?.mainVehiclePlateName ? ` — ${programming.mainVehiclePlateName}` : ''}
            </Modal.Header>
            <Modal.Content scrolling>
                <Menu pointing secondary>
                    <Menu.Item
                        name="detalhes"
                        active={activeTab === 'detalhes'}
                        onClick={() => setActiveTab('detalhes')}
                    >
                        Detalhes
                    </Menu.Item>
                    <Menu.Item
                        name="alteracoes"
                        active={activeTab === 'alteracoes'}
                        onClick={() => setActiveTab('alteracoes')}
                    >
                        Alterações
                    </Menu.Item>
                </Menu>
                {activeTab === 'alteracoes' ? (
                    auditLoading ? (
                        <Loader active inline="centered" content="Carregando histórico..." />
                    ) : auditLogs.length === 0 ? (
                        <Segment placeholder>Nenhuma alteração registrada.</Segment>
                    ) : (
                        <Table celled compact size="small">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Quando (UTC)</Table.HeaderCell>
                                    <Table.HeaderCell>Quem</Table.HeaderCell>
                                    <Table.HeaderCell>Campo</Table.HeaderCell>
                                    <Table.HeaderCell>De</Table.HeaderCell>
                                    <Table.HeaderCell>Para</Table.HeaderCell>
                                    <Table.HeaderCell>Origem</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {auditLogs.map(entry => (
                                    <Table.Row key={entry.id}>
                                        <Table.Cell>{moment(entry.changedAtUtc).utc().format('DD/MM/YYYY HH:mm')}</Table.Cell>
                                        <Table.Cell>{entry.userDisplayName} ({entry.userRole})</Table.Cell>
                                        <Table.Cell>{entry.fieldName}</Table.Cell>
                                        <Table.Cell>{entry.oldValue ?? '—'}</Table.Cell>
                                        <Table.Cell>{entry.newValue ?? '—'}</Table.Cell>
                                        <Table.Cell>{entry.source}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    )
                ) : loading && !programming ? (
                    <Loader active inline="centered" content="Carregando detalhes..." />
                ) : !programming ? (
                    <Segment placeholder>
                        <Header icon>
                            <Icon name="warning circle" />
                            Não foi possível carregar os detalhes desta programação.
                        </Header>
                    </Segment>
                ) : activeTab === 'detalhes' ? (
                    <Grid stackable>
                        <Grid.Column width={8}>
                            <Segment raised>
                                <Header as="h3">Informações da viagem</Header>
                                <p><strong>Cliente:</strong> {programming.clientName || '-'}</p>
                                <p><strong>Placa CM:</strong> {programming.mainVehiclePlateName || '-'}</p>
                                <p>
                                    <strong>Carretas:</strong>{' '}
                                    {[programming.semiTrailer1Name, programming.semiTrailer2Name].filter(Boolean).join(' / ') || '-'}
                                </p>
                                <p><strong>Motorista:</strong> {programming.driverName || '-'}</p>
                                <p><strong>Produto:</strong> {programming.productName || '-'}</p>
                                <p><strong>Volume:</strong> {StringUtils.toBrazilianDecimalFormat(programming.volume)}</p>
                                <p><strong>Tipo:</strong> {String(programming.travelType) === '0' || String(programming.travelType) === 'Empty' ? 'Vazio' : 'Carregado'}</p>
                                <Divider />
                                <p><strong>Origem:</strong> {programming.originReferenceName || '-'}</p>
                                <p><strong>Destino:</strong> {programming.destinationReferenceName || '-'}</p>
                                <p>
                                    <strong>Retorno:</strong>{' '}
                                    {programming.returnDestinationReferenceName || programming.originReferenceName || '-'}
                                </p>
                                <Divider />
                                <div style={{ marginBottom: '0.75em' }}>
                                    <strong>Status:</strong>{' '}
                                    <Label
                                        size="small"
                                        style={{ backgroundColor: statusColor, color: '#fff' }}
                                    >
                                        {statusLabel}
                                    </Label>
                                </div>
                                <p>
                                    <strong>Saída prevista:</strong>{' '}
                                    {formatProgrammingDateTime(programming.expectedOriginDepartureAt ?? programming.expectedDepartureTime)}
                                </p>
                                <p>
                                    <strong>Chegada prevista:</strong>{' '}
                                    {formatProgrammingDateTime(programming.expectedDestinationArrivalAt ?? programming.expectedArrivalTime)}
                                </p>
                                <p>
                                    <strong>ETA (planejada):</strong>{' '}
                                    {formatEstimatedArrival(programming.expectedDestinationArrivalAt ?? programming.expectedArrivalTime)}
                                </p>
                                <ProgrammingTripClosureSummary programming={programming} />
                                <Divider />
                                <Header as="h4">Linha do tempo</Header>
                                {timelineStages.map(stage => (
                                    <p key={stage.key}>
                                        <strong>{stage.label}:</strong>{' '}
                                        prev. {formatProgrammingDateTime(stage.expected)} · real {formatActualUtcAsSaoPaulo(stage.actual)}
                                    </p>
                                ))}
                                <Divider />
                                <p>
                                    <strong>Última localização:</strong>{' '}
                                    {programming.lastLocationUpdate
                                        ? moment(programming.lastLocationUpdate).format('DD/MM/YYYY HH:mm')
                                        : '-'}
                                </p>
                                <p><strong>Endereço atual:</strong> {programming.currentAddressInfo || '-'}</p>
                                {!hasCoordinates && (
                                    <Label color="orange" size="small">
                                        <Icon name="warning sign" />
                                        Sem coordenadas GPS para esta programação no momento.
                                    </Label>
                                )}
                            </Segment>
                        </Grid.Column>
                        <Grid.Column width={8}>
                            <Header as="h3">Localização do veículo</Header>
                            {hasCoordinates && locationDto ? (
                                <Segment raised>
                                    <Header as="h4">Posição atual</Header>
                                    <p><strong>Placa:</strong> {locationDto.mainVehiclePlateName || '-'}</p>
                                    <p>
                                        <strong>Coordenadas:</strong>{' '}
                                        {String(locationDto.currentLatitude ?? locationDto.latitude)},{' '}
                                        {String(locationDto.currentLongitude ?? locationDto.longitude)}
                                    </p>
                                    <p><strong>Endereço:</strong> {locationDto.currentAddressInfo || locationDto.addressInfo || '-'}</p>
                                    {(locationDto.currentLatitude ?? locationDto.latitude) != null &&
                                        (locationDto.currentLongitude ?? locationDto.longitude) != null && (
                                        <a
                                            href={`https://www.google.com/maps?q=${locationDto.currentLatitude ?? locationDto.latitude},${locationDto.currentLongitude ?? locationDto.longitude}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Abrir no Google Maps
                                        </a>
                                    )}
                                </Segment>
                            ) : (
                                <Segment placeholder>
                                    <Header icon>
                                        <Icon name="map outline" />
                                        Localização indisponível
                                    </Header>
                                </Segment>
                            )}
                        </Grid.Column>
                    </Grid>
                ) : null}
            </Modal.Content>
            <Modal.Actions>
                {programmingId && (
                    <Button
                        as="a"
                        href={`/backoffice/programming/edit/${programmingId}`}
                        color="blue"
                        icon
                        labelPosition="left"
                    >
                        <Icon name="edit" />
                        Abrir programação
                    </Button>
                )}
                <Button onClick={onClose}>Fechar</Button>
            </Modal.Actions>
        </Modal>
    );
};

export default ProgrammingDetailsModal;
